import { ExperienceLayouts, type ExperienceTheme } from '@/types/gql.generated';

export const DEFAULT_BLOCK_THEME_ID = 'nordic';

/** Style used by the planner printable layouts (plannerMonth / plannerWeek). */
export const PLANNER_STYLE_ID = 'planner';

export const hasColor = (theme: ExperienceTheme): boolean => {
  return Boolean(theme.colorType);
};

export type StyleItem = {
  id: string;
  name: string;
  isHidden?: boolean;
  backgroundColor: string;
  accentColor?: string;
  accent2Color?: string;
  textColor?: string;
  secondaryTextColor?: string;
  borderColor?: string;
  backgroundUrl?: string;
  eventTitleColor?: string;
  eventTimeColor?: string;
  eventLocationColor?: string;

  // Abstract palette for plannerMonth-style themes (optional, additive).
  // Components own the slot → function mapping (e.g. rule/year/DOW = primary1).
  // A future user style panel can override these two fields to produce a full theme.
  primary1?: string; // theme accent: rule, year, DOW headers, divider dot, date numbers
  primary2?: string; // soft tinted pastel: highlight-box fill

  neutral1?: string; // darkest ink: event name / primary text
  neutral2?: string; // date number when theme color is too light
  neutral3?: string; // day-note text
  neutral4?: string;
  neutral5?: string;
  neutral6?: string;
  neutral7?: string; // 1px gridlines
  neutral8?: string; // weekend cell shading
  neutral9?: string;
  neutral10?: string; // cell / page background
};

/**
 * Returns whether a layout uses the styleId theming system (block themes).
 * Layouts that return false use the backgroundUrl system instead.
 */
export function layoutUsesStyleId(layout: ExperienceLayouts): boolean {
  switch (layout) {
    case ExperienceLayouts.PrintableYearly3BoxMonthCard:
    case ExperienceLayouts.PrintableYearly6BoxMonthCard:
    case ExperienceLayouts.PrintableYearlyMonthCard:
      return true;
    default:
      return false;
  }
}

// Theme Configuration
export const blockStyles: readonly StyleItem[] = [
  {
    id: 'nordic',
    name: 'Nordic',
    backgroundColor: '#E4EAF3',
    accentColor: '#5B6AA5',
    accent2Color: '#E4EAF3',
    textColor: '#FFFFFF',
  },
  {
    id: 'honeycomb',
    name: 'Honeycomb',
    backgroundColor: '#F1EDE9',
    accentColor: '#FFC255',
    accent2Color: '#F1EDE9',
    textColor: '#000000',
  },
  {
    id: 'soft-serve',
    name: 'Soft Serve',
    backgroundColor: '#F1EDE9',
    accentColor: '#FFA6B9',
    accent2Color: '#F1EDE9',
    textColor: '#000000',
  },
  {
    id: 'stone',
    name: 'Stone',
    backgroundColor: '#FFFFFF',
    accentColor: '#5D6B7E',
    accent2Color: '#FFFFFF',
    textColor: '#FFFFFF',
  },
  {
    id: 'raspberry',
    name: 'Raspberry',
    backgroundColor: '#F8E8F0',
    accentColor: '#C85A8E',
    accent2Color: '#F8E8F0',
    textColor: '#FFFFFF',
  },
  {
    id: 'harbor',
    name: 'Harbor',
    backgroundColor: '#F1EDE9',
    accentColor: '#134E88',
    accent2Color: '#F1EDE9',
    textColor: '#FFFFFF',
  },
  {
    id: 'eucalyptus',
    name: 'Eucalyptus',
    backgroundColor: '#F1EDE9',
    accentColor: '#207868',
    accent2Color: '#F1EDE9',
    textColor: '#FFFFFF',
  },
  {
    id: 'tuxedo',
    name: 'Tuxedo',
    backgroundColor: '#FFFFFF',
    accentColor: '#000',
    textColor: '#FFFFFF',
    accent2Color: '#FAFAFA',
  },
  {
    id: 'pewter',
    name: 'Pewter',
    backgroundColor: '#F0F1F3',
    accentColor: '#5D6B7E',
    accent2Color: '#F0F1F3',
    textColor: '#FFFFFF',
  },
  {
    id: 'succulent',
    name: 'Succulent',
    backgroundColor: '#F1EDE9',
    accentColor: '#7FB8A6',
    accent2Color: '#F1EDE9',
    textColor: '#000000',
  },
  {
    id: 'orchid',
    name: 'Orchid',
    backgroundColor: '#F1EDE9',
    accentColor: '#9B6BA8',
    accent2Color: '#F1EDE9',
    textColor: '#FFFFFF',
  },
  {
    id: 'paper',
    name: 'Paper',
    backgroundColor: '#FFFFFF',
    accentColor: '#FFFFFF',
    accent2Color: '#FFFFFF',
    textColor: '#000000',
  },
  {
    id: 'planner',
    name: 'Planner',
    isHidden: true,
    backgroundColor: '#FFFFFF',
    accentColor: '#c8c4bf',
    accent2Color: '#E0DCD8',
    textColor: '#1a1a1a',
    secondaryTextColor: '#9a948f',
    borderColor: '#e0dcd8',
    eventTitleColor: '#6B6560',
    eventTimeColor: '#9a948f',
    eventLocationColor: '#c8c4bf',

    neutral1: '#1a1a1a',
    neutral2: '#2D2D2D',
    neutral3: '#6B6560',
    neutral4: '#9a948f',
    neutral5: '#C8C4BF',
    neutral6: '#E0DCD8',
    neutral7: '#E8E4E0',
    neutral8: '#F7F5F3',
    neutral9: '#F0EDEA',
    neutral10: '#FFFFFF',
  },
  {
    id: 'summer_pink',
    name: 'Pink',
    isHidden: true,
    backgroundColor: '#FFFFFF',
    accentColor: '#D5257C',
    accent2Color: '#E0DCD8',
    textColor: '#1a1a1a',
    secondaryTextColor: '#9a948f',
    borderColor: '#e0dcd8',
    eventTitleColor: '#1a1a1a',

    // Abstract palette — Pink #D5257C (warm, hue ~358, L ~0.55)
    primary1: '#D5257C', // theme accent
    primary2: '#FFECF4', // highlight-box tint (blockout fill)
    neutral1: '#1a1a1a',
    neutral2: '#2B2B2B', // date number when theme too light (not applicable to Pink)
    neutral3: '#5a5a5a', // day-note text
    neutral4: '#9a948f',
    neutral5: '#C8C4BF',
    neutral6: '#E0DCD8',
    neutral7: '#E7E4E0', // 1px gridlines (warm variant)
    neutral8: '#F8F7F5', // weekend cell shading (warm variant)
    neutral9: '#F0EDEA',
    neutral10: '#FFFFFF',
  },
];

// Build a lookup map of all styles at module load time
const styleMap: Record<string, StyleItem> = Object.fromEntries(
  blockStyles.map((item) => [item.id, item])
);

/**
 * Returns the full style object for a given style id.
 * Returns undefined if not found.
 */
export function getStyleById(id: string = DEFAULT_BLOCK_THEME_ID): StyleItem {
  return styleMap[id] ?? styleMap[DEFAULT_BLOCK_THEME_ID];
}
